import { Dispatch } from "redux";
import { Observable } from "rxjs";
import _ from "lodash";
import { baseActionEpic, progressEpic } from "epics/base-epic";
import { NOTIFICATIONS } from "constants/action-types";
import { IStore } from "reducers";
import {
    IFeedEntry,
    IFeedNotifications,
    IFeedsFilters,
    INotificationFilters,
    INotification,
} from "reducers/notifications";
import { Slug } from "constants/notifications";
import siteConfig from "site-config";
import me from "models/me";
import { stringify } from "query-string";
import * as actions from "actions/notification-actions";

const POLL_INTERVAL = 60 * 1000; // one minute

export const poll = (action$, store: { getState: () => IStore; dispatch: Dispatch<{}> }) =>
    action$.ofType(NOTIFICATIONS.POLL.START).switchMap(({ payload: slugs }) =>
        Observable.interval(POLL_INTERVAL)
            .startWith(0)
            .takeUntil(action$.ofType(NOTIFICATIONS.POLL.STOP))
            .do(() => store.dispatch(actions.fetchFeeds({ slugs })))
            .flatMap(() =>
                action$
                    .ofType(NOTIFICATIONS.FEEDS.FETCH.SUCCESS)
                    .take(1)
                    .mergeMap(({ payload: feeds }: { payload: IFeedEntry[] }) =>
                        _(
                            slugs
                                ? _.intersectionWith(feeds, slugs, ({ feed_slug_id }, slug) => feed_slug_id === slug)
                                : feeds,
                        ) // TODO: manual filtering until backend supports query filters
                            .map(({ feed_slug_id, unread, unseen }) =>
                                // fetch notifications if there are unread/unseen ones or the first call
                                Number(unread) > 0 ||
                                Number(unseen) > 0 ||
                                !store.getState().notifications.feedNotifications.payload?.[feed_slug_id]
                                    ? actions.fetchNotifications(feed_slug_id)
                                    : null,
                            )
                            .compact()
                            .value(),
                    ),
            ),
    );

export const fetch = progressEpic<{ slug: Slug; params: INotificationFilters }, IFeedNotifications>(
    NOTIFICATIONS.FEED_NOTIFICATIONS.FETCH,
    ({ payload: { slug, params } }) => ({
        method: "GET",
        url: `${siteConfig.goAPI}/v1/user_notification/users/${me._id}/feeds/${slug}/items?${stringify(params)}`,
    }),
    {
        mapper: "flatMap",
        dataCustomizer: (
            { notifications }: { notifications: INotification[] },
            { payload: { params, slug } },
            store: IStore,
        ) =>
            ({
                [slug]: {
                    list: params?.start_id_open
                        ? [...(store.notifications.feedNotifications.payload?.[slug]?.list || []), ...notifications]
                        : notifications,
                    hasNextPage: _.size(notifications) === params?.limit,
                },
            }) as IFeedNotifications,
        nextPageGetter: null,
    },
);

export const updateStatus = baseActionEpic<{ slug: Slug; params: INotificationFilters }, IFeedNotifications>(
    NOTIFICATIONS.FEED_NOTIFICATIONS.STATUS,
    ({ payload: { slug, params } }) => ({
        method: "GET",
        url: `${siteConfig.goAPI}/v1/user_notification/users/${me._id}/feeds/${slug}/items?${stringify(params, {
            arrayFormat: "comma",
        })}`,
    }),
    () => [],
    { mapper: "flatMap" },
);

export const fetchFeeds = progressEpic<IFeedsFilters, IFeedEntry[]>(
    NOTIFICATIONS.FEEDS.FETCH,
    ({ payload }) => ({
        method: "GET",
        url: `${siteConfig.goAPI}/v1/user_notification/users/${me._id}/feeds?${stringify(payload)}`,
    }),
    {
        dataCustomizer: ({ feeds }, { payload }, store: IStore) =>
            payload?.start_id_open ? [...feeds, ...(store.notifications.feeds.payload || [])] : feeds,
        nextPageGetter: ({ feeds }, { payload }) => ({ hasNextPage: _.size(feeds) >= payload?.limit }),
    },
);
